// web/src/pages/ClaimUsername.tsx
import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { auth, db } from "@/lib/firebase";
import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  type DocumentReference,
  type DocumentData,
} from "firebase/firestore";

/* =========================
   Types & helpers
========================= */

type Stage = "idle" | "checking" | "reserving";

const USERNAME_RE = /^[a-z0-9_]{3,32}$/;

function normalizeUsername(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "_");
}

async function checkUsernameAvailable(slug: string): Promise<boolean> {
  const ref = doc(db, "usernames", slug);
  const snap = await getDoc(ref);
  return !snap.exists();
}

/** Reserve username in a single transaction:
 *  1) Read usernames/{slug} and users/{uid}
 *  2) If usernames/{slug} exists -> throw
 *  3) If users/{uid}.username already set -> throw
 *  4) Create usernames/{slug} and set users/{uid}.username atomically
 */
async function reserveUsernameTx(uid: string, slug: string): Promise<void> {
  const usernameRef: DocumentReference<DocumentData> = doc(
    db,
    "usernames",
    slug,
  );
  const userRef: DocumentReference<DocumentData> = doc(db, "users", uid);

  await runTransaction(db, async (tx) => {
    // ---- ALL READS FIRST ----
    const [unameSnap, userSnap] = await Promise.all([
      tx.get(usernameRef),
      tx.get(userRef),
    ]);

    if (unameSnap.exists()) {
      throw new Error("This username has already been taken.");
    }

    const userData = userSnap.exists()
      ? (userSnap.data() as {
          username?: string | null;
          display_name?: string | null;
          displayName?: string | null;
        })
      : {};
    if (userData.username) {
      throw new Error("You have already set a username.");
    }

    // ---- WRITES AFTER READS ----
    // Create the mapping (rules allow create only; update is denied)
    tx.set(usernameRef, {
      uid,
      createdAt: serverTimestamp(),
    });

    // Set username on the user profile (allowed once by rules)
    const displayName =
      (userData.display_name ?? userData.displayName ?? slug) || slug;
    tx.set(
      userRef,
      {
        username: slug,
        username_lower: slug,
        display_name: displayName,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  });
}

/* =========================
   Page
========================= */

export default function ClaimUsername(): JSX.Element {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/settings";
  const ret = params.get("return") || "/";

  const [value, setValue] = useState<string>("");
  const slug = useMemo(() => normalizeUsername(value), [value]);

  const [stage, setStage] = useState<Stage>("idle");
  const [available, setAvailable] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Live availability check with debounce
  useEffect(() => {
    let alive = true;

    async function run(): Promise<void> {
      setError(null);
      if (!slug || !USERNAME_RE.test(slug)) {
        setAvailable(null);
        return;
      }
      setStage("checking");
      try {
        const ok = await checkUsernameAvailable(slug);
        if (alive) setAvailable(ok);
      } catch (e) {
        if (alive) {
          setAvailable(null);
        }
      } finally {
        if (alive) setStage("idle");
      }
    }

    const t = setTimeout(() => void run(), 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [slug]);

  async function onReserve(): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      setError("You need to be signed in.");
      return;
    }

    const candidate = slug;
    if (!USERNAME_RE.test(candidate)) {
      setError("Usernames must be 3–32 chars: a–z, 0–9, underscore.");
      return;
    }

    setError(null);
    setStage("reserving");
    try {
      await reserveUsernameTx(user.uid, candidate);

      // After claiming the username, go to Terms/Ack.
      // Users accept terms next; on accept, continue to "next" (default /settings).
      nav(
        `/termsack?next=${encodeURIComponent(next)}&return=${encodeURIComponent(ret)}`,
        {
          replace: true,
        },
      );
    } catch (e) {
      const msg =
        (e as { message?: string }).message ??
        "Could not reserve that username. Please try another.";
      setError(msg);
    } finally {
      setStage("idle");
    }
  }

  return (
    <main className="mx-auto max-w-2xl px-4 py-10">
      <div className="inline-grid place-items-center w-16 h-16 rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-600 text-white text-2xl font-bold">
        SG
      </div>
      <h1 className="mt-4 text-3xl font-bold text-white">
        Claim your username
      </h1>
      <p className="mt-2 text-white/80">
        Your username is public and permanent. Choose wisely.
      </p>

      <div className="mt-6 grid gap-3">
        <label className="text-sm text-white/80" htmlFor="username-input">
          Username (a–z, 0–9, _; 3–32 chars)
        </label>
        <input
          id="username-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="e.g. planeswalker_92"
          className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-white"
          autoCapitalize="off"
          autoComplete="off"
          spellCheck={false}
          inputMode="text"
        />
        {/* Availability status */}
        {value.trim() !== "" && USERNAME_RE.test(slug) && (
          <div className="text-sm">
            {stage === "checking" ? (
              <span className="text-white/70">Checking availability…</span>
            ) : available === true ? (
              <span className="text-emerald-400">Available</span>
            ) : available === false ? (
              <span className="text-rose-400">Taken</span>
            ) : null}
          </div>
        )}

        {error && <div className="text-amber-300 text-sm">{error}</div>}

        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={() => void onReserve()}
            disabled={
              stage !== "idle" || !USERNAME_RE.test(slug) || available === false
            }
            className="rounded-md bg-fuchsia-600 px-4 py-2 text-white font-semibold hover:bg-fuchsia-700 disabled:opacity-60"
            aria-disabled={
              stage !== "idle" || !USERNAME_RE.test(slug) || available === false
            }
          >
            Reserve username
          </button>
          <Link
            to={ret}
            className="rounded-md border border-white/10 bg-white/5 px-4 py-2 text-white hover:bg-white/10"
          >
            Cancel
          </Link>
        </div>

        <p className="mt-6 text-xs text-white/60">
          By reserving a username, you agree that it is permanent. You’ll review
          and accept our{" "}
          <Link
            className="underline"
            to="/legal/terms"
            target="_blank"
            rel="noreferrer"
          >
            Terms of Service
          </Link>{" "}
          on the next step.
        </p>
      </div>
    </main>
  );
}

